/*
 * bioverde-global.js
 *
 * The only globally-loaded script in the theme. Contains Dawn's accessibility
 * helpers, retained verbatim, plus a small reveal observer.
 *
 * Everything else in this theme is a section-scoped ES module. Nothing here
 * touches cart, price, variants or customer accounts — those subsystems do not
 * exist in this build.
 */

/* ------------------------------------------------------------------
 * Dawn accessibility helpers (kept as-is)
 * ------------------------------------------------------------------ */

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) mouseClick = false;
  });

  window.addEventListener('mousedown', () => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');
      if (mouseClick) return;
      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling && summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });
});

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/* ------------------------------------------------------------------
 * Shared motion utilities
 *
 * prefersReducedMotion is read live rather than cached, so a buyer who
 * changes the OS setting mid-session gets the new behaviour without a reload.
 * ------------------------------------------------------------------ */

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

window.Bioverde = window.Bioverde || {};
window.Bioverde.prefersReducedMotion = () => reducedMotionQuery.matches;

/* rAF-throttled callback factory. Used by every scroll handler in the theme so
 * that no handler runs more than once per frame. */
window.Bioverde.rafThrottle = function rafThrottle(fn) {
  let queued = false;
  let lastArgs = null;
  return function throttled(...args) {
    lastArgs = args;
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      fn.apply(this, lastArgs);
    });
  };
};

/* ------------------------------------------------------------------
 * Reveal observer
 *
 * Ported from the prototype: .reveal elements gain .in once, at threshold .15.
 * Re-scanned on shopify:section:load so sections added or edited in the theme
 * editor animate in rather than sitting at opacity:0 forever.
 *
 * Under prefers-reduced-motion no observer is created and everything is
 * revealed immediately; the stylesheet already neutralises .reveal, this is
 * belt and braces for the case where JS runs before CSS applies.
 * ------------------------------------------------------------------ */

class RevealObserver {
  constructor() {
    this.observer = null;
    this.init();
    document.addEventListener('shopify:section:load', () => this.scan());
  }

  init() {
    if (window.Bioverde.prefersReducedMotion() || !('IntersectionObserver' in window)) {
      this.revealAll();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          this.observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15 }
    );

    this.scan();
  }

  scan() {
    if (!this.observer) {
      this.revealAll();
      return;
    }
    document.querySelectorAll('.reveal:not(.in)').forEach((el) => this.observer.observe(el));
  }

  revealAll() {
    document.querySelectorAll('.reveal').forEach((el) => el.classList.add('in'));
  }
}

/* ------------------------------------------------------------------
 * Loader and sticky nav — ported from the prototype's inline script.
 *
 * The loader is hidden by the theme editor (it would cover the canvas on
 * every re-render) and is skipped entirely under reduced motion.
 * ------------------------------------------------------------------ */

function initLoader() {
  const loader = document.getElementById('loader');
  if (!loader) return;

  const dismiss = () => loader.classList.add('done');

  if (window.Shopify && window.Shopify.designMode) {
    dismiss();
    return;
  }
  if (window.Bioverde.prefersReducedMotion()) {
    dismiss();
    return;
  }

  // Fires even if `load` already happened before this script ran.
  if (document.readyState === 'complete') {
    setTimeout(dismiss, 800);
  } else {
    window.addEventListener('load', () => setTimeout(dismiss, 800), { once: true });
  }

  // Never let a stalled asset trap a buyer behind the loader.
  setTimeout(dismiss, 5000);
}

function initNav() {
  const nav = document.getElementById('nav');
  if (!nav) return;

  const apply = () => nav.classList.toggle('solid', window.scrollY > 40);
  apply();
  window.addEventListener('scroll', window.Bioverde.rafThrottle(apply), { passive: true });
}

/* ------------------------------------------------------------------
 * Enquiry confirmation
 *
 * Shopify returns the buyer to the page with ?contact_posted=true. The
 * confirmation renders server-side; this moves focus to it so the outcome is
 * announced to a screen reader rather than left for the buyer to find.
 * ------------------------------------------------------------------ */

function initEnquiryConfirmation() {
  const success = document.querySelector('[data-enquiry-success]');
  if (!success) return;
  success.focus({ preventScroll: false });
}

function boot() {
  window.Bioverde.reveal = new RevealObserver();
  initLoader();
  initNav();
  initEnquiryConfirmation();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// The header section re-renders as a whole in the editor, taking #nav with it.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('#nav')) initNav();
});
